import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Search } from "lucide-react";
import { api, shopDateTime } from "@/lib/api";
import { WaitingOnCustomer } from "@/components/WaitingOnCustomer";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CollectKycDialog } from "@/components/CollectKycDialog";

const STATUS = {
  submitted: "bg-[#C5A059] text-[#0A0A0A]",
  verified: "bg-[#166534] text-white",
  rejected: "bg-[#9A3412] text-white",
};

const FILTERS = [
  { key: "", label: "Everything" },
  { key: "submitted", label: "Awaiting check" },
  { key: "verified", label: "Verified" },
  { key: "rejected", label: "Rejected" },
];

export default function KycList() {
  const { branch } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [allBranches, setAllBranches] = useState(true);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takeOpen, setTakeOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get("/kyc", {
        params: {
          ...(status ? { status } : {}),
          ...(allBranches ? {} : { branch }),
          ...(q.trim() ? { q: q.trim() } : {}),
        },
      })
      .then(({ data }) => setRows(data))
      .catch(() => toast.error("Could not load the KYC forms"))
      .finally(() => setLoading(false));
  }, [status, allBranches, branch, q]);

  return (
    <div className="fade-up" data-testid="kyc-list">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {allBranches ? "All branches" : branch}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">KYC Forms</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            ID checks for the AML file — needed when a customer pays close to $10,000 in cash,
            or when you are buying gold from them. Send the request by WhatsApp from the
            invoice or contract; the customer fills it in on their own phone, ID photos
            included. Check and verify each one.
          </p>
        </div>
        <Button
          className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          onClick={() => setTakeOpen(true)}
          data-testid="take-kyc-button"
        >
          <ClipboardCheck className="mr-1.5 h-4 w-4" /> Take KYC
        </Button>
      </div>

      <WaitingOnCustomer />

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="kyc-filters">        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            onClick={() => setStatus(f.key)}
            className={`border px-4 py-2 text-sm transition-colors duration-150 ${
              status === f.key
                ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                : "border-border hover:bg-muted"
            }`}
            data-testid={`kyc-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <label className="flex items-center gap-2 px-2 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            data-testid="kyc-all-branches"
          />
          Both stores
        </label>
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 w-[250px] rounded-none pl-9"
            placeholder="Name, mobile, licence or passport"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="kyc-search-input"
          />
        </div>
      </div>

      <div className="mt-5 overflow-x-auto border border-border">
        <table className="w-full min-w-[900px] text-sm" data-testid="kyc-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {["Submitted", "Customer", "Name as per passport", "Mobile", "Licence", "Passport",
                "IDs", "Status"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                  {loading ? "Loading…" : "No KYC forms yet."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className="cursor-pointer hover:bg-[#FBF8F2]"
                onClick={() => navigate(`/kyc/${r.id}`)}
                data-testid={`kyc-row-${r.id}`}
              >
                <td className="tabular px-3 py-2.5 text-xs text-muted-foreground">
                  {shopDateTime(r.submitted_at)}
                  <div>{r.branch}</div>
                </td>
                <td className="px-3 py-2.5 font-semibold">
                  <Link to={`/kyc/${r.id}`} className="hover:underline">
                    {r.customer_name}
                  </Link>
                </td>
                <td className="px-3 py-2.5">{r.name_as_per_passport}</td>
                <td className="tabular px-3 py-2.5">{r.whatsapp_mobile}</td>
                <td className="px-3 py-2.5">{r.licence_number || "-"}</td>
                <td className="px-3 py-2.5">{r.passport_number || "-"}</td>
                <td className="px-3 py-2.5">{(r.attachments || []).length}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`px-2 py-1 text-[10px] font-bold uppercase ${
                      STATUS[r.status] || "bg-muted"
                    }`}
                    data-testid={`kyc-status-${r.id}`}
                  >
                    {r.status}
                  </span>
                  {r.id_expired && (
                    <span className="ml-2 text-[10px] font-bold uppercase text-[#9A3412]">
                      ID expired
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CollectKycDialog open={takeOpen} onClose={() => setTakeOpen(false)} />
    </div>
  );
}
